/**
 * Conversa com a API PHP (/api/*.php). Toda chamada passa por `chamar`, que:
 * - manda o cabeçalho de segurança "X-Alianca: 1";
 * - transforma um erro da API em ErroApi { codigo, mensagem, pedido }, que as
 *   telas mostram (o "pedido" é o id que aparece no log do servidor).
 *
 * Lê: a API (rede).
 * Usado por: telas/ e dados/usar-dados-do-mes.ts.
 */
import type { Cadastros, Carteira, ContagemCarteira, DataIso, Fonte, Importacao, LinhaVenda, Meta, Mes, Usuario, VendaSalva } from '../dominio/tipos.ts';

export class ErroApi extends Error {
  readonly codigo: string;
  readonly status: number;
  readonly pedido: string | null;

  constructor(codigo: string, mensagem: string, status: number, pedido: string | null) {
    super(mensagem);
    this.name = 'ErroApi';
    this.codigo = codigo;
    this.status = status;
    this.pedido = pedido;
  }
}

/** Avisado quando a sessão expira (a tela volta para o login). */
let aoPerderSessao: (() => void) | null = null;
export function quandoPerderSessao(funcao: () => void): void {
  aoPerderSessao = funcao;
}

/** Avisado quando a API pede a troca da senha provisória (API-428): a tela vai para "Crie a sua senha". */
let aoPedirSenhaNova: (() => void) | null = null;
export function quandoPedirSenhaNova(funcao: () => void): void {
  aoPedirSenhaNova = funcao;
}

export interface ErroVisto {
  codigo: string;
  mensagem: string;
  pedido: string | null;
  caminho: string;
  /** Data e hora (ISO) em que aconteceu. */
  quando: string;
}

/** O último erro visto (vai junto no "Relatar", com o código do pedido que leva ao log). */
let ultimoErro: ErroVisto | null = null;

/** Guarda o erro para o próximo relato e devolve ele mesmo (para o throw). */
export function anotarErro(falha: ErroApi, caminho: string): ErroApi {
  ultimoErro = { codigo: falha.codigo, mensagem: falha.message, pedido: falha.pedido, caminho: caminho.split('?')[0] ?? caminho, quando: new Date().toISOString() };
  return falha;
}

/** O último erro, se aconteceu nos últimos 15 minutos (um erro velho não explica o problema de agora). */
export function ultimoErroDaApi(): ErroVisto | null {
  if (!ultimoErro || Date.now() - Date.parse(ultimoErro.quando) > 15 * 60_000) return null;
  return ultimoErro;
}

async function chamar<T>(caminho: string, opcoes: { metodo?: 'GET' | 'POST' | 'DELETE'; corpo?: unknown } = {}): Promise<T> {
  let resposta: Response;
  try {
    resposta = await fetch(`/api/${caminho}`, {
      method: opcoes.metodo ?? 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Alianca': '1',
        ...(opcoes.corpo === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: opcoes.corpo === undefined ? undefined : JSON.stringify(opcoes.corpo),
    });
  } catch {
    throw anotarErro(
      new ErroApi('REDE', 'Sem conexão com o sistema. Confira a internet; se ela estiver funcionando, o sistema pode estar fora do ar: tente de novo em alguns minutos.', 0, null),
      caminho,
    );
  }
  const texto = await resposta.text();
  let dados: unknown = {};
  try {
    dados = texto === '' ? {} : JSON.parse(texto);
  } catch {
    throw anotarErro(
      new ErroApi(`HTTP-${resposta.status}`, `O sistema não respondeu como deveria (erro ${resposta.status}). Espere um minuto e tente de novo; se continuar, use o botão Relatar.`, resposta.status, null),
      caminho,
    );
  }
  if (!resposta.ok) {
    const erro = (dados as { erro?: { codigo?: string; mensagem?: string; pedido?: string } }).erro;
    if (resposta.status === 401 && caminho !== 'sessao.php') aoPerderSessao?.();
    if (resposta.status === 428) aoPedirSenhaNova?.();
    throw anotarErro(new ErroApi(erro?.codigo ?? `HTTP-${resposta.status}`, erro?.mensagem ?? 'Erro no servidor.', resposta.status, erro?.pedido ?? null), caminho);
  }
  return dados as T;
}

export interface NovaImportacao {
  fonte: Fonte;
  /** Marca do arquivo (null = arquivo geral, com todas as marcas). */
  marcaId: number | null;
  leitor: string;
  nomeArquivo: string;
  hashArquivo: string;
  periodo: { inicio: DataIso; fim: DataIso };
  linhas: LinhaVenda[];
  linhasIgnoradas: number;
  /** O relatório que a gerente viu antes de salvar (guardado para consulta). */
  relatorio: Record<string, unknown>;
}

export interface NovaCarteira {
  fonte: Fonte;
  dataReferencia: DataIso;
  nomeArquivo: string;
  hashArquivo: string;
  /** Só as contagens: nenhum dado de cliente sai do navegador. */
  contagens: ContagemCarteira[];
}

export interface DadosRepresentante {
  id?: number;
  nome: string;
  ativo: boolean;
  entraNoRanking: boolean;
  /** Códigos ligados junto (ao criar): { fonte, codigo, desde (null = desde sempre) }. */
  codigos?: Array<{ fonte: Fonte; codigo: string; desde: Mes | null }>;
}

/** "Saiu da equipe": quem sai, quem entra (alguém que já existe ou um nome novo) e a partir de quando. */
export interface TrocaDeRepresentante {
  saiId: number;
  entraId?: number;
  novoNome?: string;
  desde: Mes;
  codigos: Array<{ fonte: Fonte; codigo: string }>;
  moverMetas: boolean;
}

/** Um usuário do sistema, como a tela Sistema → Usuários vê. */
export interface UsuarioDoSistema {
  id: number;
  email: string;
  nome: string;
  papel: 'gerente' | 'admin';
  ativo: boolean;
  senhaProvisoria: boolean;
  criadoEm: string;
}

export type TipoDeRelato = 'problema' | 'sugestao' | 'duvida';

export interface Relato {
  id: number;
  criadoEm: string;
  criadoPor: string;
  tipo: TipoDeRelato;
  texto: string;
  tela: string;
  contexto: Record<string, unknown>;
  situacao: 'aberto' | 'resolvido';
  resposta: string | null;
  respondidoEm: string | null;
  respondidoPor: string | null;
}

export interface Alteracao {
  id: number;
  momento: string;
  usuario: string | null;
  acao: string;
  detalhe: Record<string, unknown>;
}

/** A saúde do servidor (tela Sistema). */
export interface InfoDoSistema {
  versao: string;
  php: string;
  sqlite: string;
  limites: Record<string, string>;
  /** foraDoSite: null no servidor de desenvolvimento (não se aplica). */
  pastaDeDados: { foraDoSite: boolean | null; gravavel: boolean };
  banco: { tamanho: number; migracoes: Array<{ nome: string; aplicadaEm: string }>; contagens: Record<string, number> };
  copias: Array<{ arquivo: string; tamanho: number }>;
  alteracoes: Alteracao[];
}

const cadastrosPost = <T = Cadastros>(acao: string, dados: Record<string, unknown> = {}) => chamar<T>('cadastros.php', { metodo: 'POST', corpo: { acao, ...dados } });

export const api = {
  sessao: () => chamar<{ usuario: Usuario | null; logo: string | null; versao: string }>('sessao.php'),
  entrar: (email: string, senha: string) => chamar<{ usuario: Usuario }>('sessao.php', { metodo: 'POST', corpo: { email, senha } }),
  sair: () => chamar<{ ok: true }>('sessao.php', { metodo: 'DELETE' }),
  /** Primeiro acesso: o sistema ainda não tem usuário? */
  instalacao: () => chamar<{ precisaInstalar: boolean; podeInstalar: boolean }>('instalacao.php'),
  instalar: (dados: { codigo: string; nome: string; email: string; senha: string }) => chamar<{ usuario: Usuario }>('instalacao.php', { metodo: 'POST', corpo: dados }),
  trocarMinhaSenha: (senhaAtual: string, senhaNova: string) =>
    chamar<{ usuario: Usuario }>('usuarios.php', { metodo: 'POST', corpo: { acao: 'trocarMinhaSenha', senhaAtual, senhaNova } }),

  usuarios: () => chamar<{ usuarios: UsuarioDoSistema[] }>('usuarios.php'),
  criarUsuario: (dados: { email: string; nome: string; papel: 'gerente' | 'admin' }) =>
    chamar<{ usuarios: UsuarioDoSistema[]; senhaProvisoria: string }>('usuarios.php', { metodo: 'POST', corpo: { acao: 'criar', ...dados } }),
  salvarUsuario: (dados: { id: number; nome: string; papel: 'gerente' | 'admin'; ativo: boolean }) =>
    chamar<{ usuarios: UsuarioDoSistema[] }>('usuarios.php', { metodo: 'POST', corpo: { acao: 'salvar', ...dados } }),
  redefinirSenha: (id: number) => chamar<{ usuarios: UsuarioDoSistema[]; senhaProvisoria: string }>('usuarios.php', { metodo: 'POST', corpo: { acao: 'redefinirSenha', id } }),

  sistema: () => chamar<InfoDoSistema>('sistema.php'),
  relatos: () => chamar<{ relatos: Relato[] }>('relatos.php'),
  criarRelato: (dados: { tipo: TipoDeRelato; texto: string; tela: string; contexto: Record<string, unknown> }) =>
    chamar<{ relato: Relato }>('relatos.php', { metodo: 'POST', corpo: { acao: 'criar', ...dados } }),
  /** Só administrador: resposta (acrescentada com nome e data) e situação. */
  responderRelato: (id: number, situacao: 'aberto' | 'resolvido', resposta: string) =>
    chamar<{ relato: Relato }>('relatos.php', { metodo: 'POST', corpo: { acao: 'responder', id, situacao, resposta } }),
  /** Quem relatou acrescenta algo (o relato reabre). */
  complementarRelato: (id: number, texto: string) => chamar<{ relato: Relato }>('relatos.php', { metodo: 'POST', corpo: { acao: 'complementar', id, texto } }),

  cadastros: () => chamar<Cadastros>('cadastros.php'),
  salvarRepresentante: (dados: DadosRepresentante) => cadastrosPost('salvarRepresentante', { ...dados }),
  excluirRepresentante: (id: number) => cadastrosPost('excluirRepresentante', { id }),
  trocarRepresentante: (dados: TrocaDeRepresentante) => cadastrosPost<Cadastros & { entraId: number }>('trocarRepresentante', { ...dados }),
  /** chave = "logo" ou "representante-<id>"; imagem = data URL (já reduzida em componentes/EscolherImagem.tsx). */
  salvarImagem: (chave: string, imagem: string) => cadastrosPost('salvarImagem', { chave, imagem }),
  removerImagem: (chave: string) => cadastrosPost('removerImagem', { chave }),
  salvarFeriado: (data: DataIso, nome: string) => cadastrosPost('salvarFeriado', { data, nome }),
  removerFeriado: (data: DataIso) => cadastrosPost('removerFeriado', { data }),
  /** desde = mês a partir do qual o código é desta pessoa (null = desde sempre). */
  vincularCodigo: (fonte: Fonte, codigo: string, representanteId: number, desde: Mes | null) =>
    chamar<Cadastros>('cadastros.php', { metodo: 'POST', corpo: { acao: 'vincularCodigo', fonte, codigo, representanteId, desde } }),
  desvincularCodigo: (fonte: Fonte, codigo: string, desde: Mes | null) =>
    chamar<Cadastros>('cadastros.php', { metodo: 'POST', corpo: { acao: 'desvincularCodigo', fonte, codigo, desde } }),
  salvarMarca: (dados: { id?: number; nome: string; fonte: Fonte | null; ativa: boolean; ordem: number }) =>
    chamar<Cadastros>('cadastros.php', { metodo: 'POST', corpo: { acao: 'salvarMarca', ...dados } }),

  metas: (mes: Mes) => chamar<{ mes: Mes; metas: Meta[] }>(`metas.php?mes=${encodeURIComponent(mes)}`),
  /** Grava só os pares enviados; valor 0 apaga aquela meta. */
  /** antes = o valor quando a grade abriu (o servidor recusa com API-409 se já mudou lá). */
  salvarMetas: (mes: Mes, metas: Array<Omit<Meta, 'mes'> & { antes?: number }>) => chamar<{ ok: true; quantidade: number }>('metas.php', { metodo: 'POST', corpo: { mes, metas } }),

  vendas: (inicio: DataIso, fim: DataIso, fonte: Fonte) =>
    chamar<{ vendas: VendaSalva[] }>(`vendas.php?inicio=${inicio}&fim=${fim}&fonte=${encodeURIComponent(fonte)}`),

  importacoes: () => chamar<{ importacoes: Importacao[] }>('importacoes.php'),
  importacao: (id: number) => chamar<{ importacao: Importacao & { relatorio: Record<string, unknown> } }>(`importacoes.php?id=${id}`),
  importacoesComHash: (hash: string) => chamar<{ importacoes: Importacao[] }>(`importacoes.php?hash=${encodeURIComponent(hash)}`),
  criarImportacao: (dados: NovaImportacao) => chamar<{ importacao: Importacao }>('importacoes.php', { metodo: 'POST', corpo: { acao: 'criar', ...dados } }),
  desfazerImportacao: (id: number) => chamar<{ ok: true }>('importacoes.php', { metodo: 'POST', corpo: { acao: 'desfazer', id } }),

  carteiras: () => chamar<{ carteiras: Carteira[] }>('carteiras.php'),
  criarCarteira: (dados: NovaCarteira) => chamar<{ carteira: Carteira }>('carteiras.php', { metodo: 'POST', corpo: { acao: 'criar', ...dados } }),
  desfazerCarteira: (id: number) => chamar<{ ok: true }>('carteiras.php', { metodo: 'POST', corpo: { acao: 'desfazer', id } }),
};
